<template>
  <BaseDialog max-width="800" persistent :value="true">
    <template #modal>
      <BaseCosmoCard>
        <template #title>
          <div class="modal_header">
            <BaseTitle v-text="modalTitle" />
            <BaseButtonIcon
              class="close_icon"
              icon="$mdiClose"
              :disabled="isSubmiting"
              @click="reset()"
            />
          </div>
        </template>
        <div class="modal_content">
          <div v-on="$listeners">
            <div class="sub-title" v-text="$t('folder.offer.offer_type')" />
            <div class="input-fields">
              <folder-offer-selecte-pad
                :select-items="offerTypes"
                :has-error="typeError"
                :default-value="offerDefaultValue"
                :disabled="!!offerTemplateId"
                @selected="handleOfferTypeChange($event)"
              />
              <div class="offer-nature-container">
                <v-skeleton-loader
                  v-if="isLoading && !form.name"
                  type="text"
                  class="offer-item"
                />
                <BaseTextField
                  v-else
                  v-model="form.name"
                  autofocus
                  dense
                  name="name"
                  :label="`${$t('folder.offerName')} *`"
                  :error-messages="nameErrors"
                  :has-error="!!nameErrors"
                  class="offer-item"
                />
              </div>
              <span v-text="$t('folder.offerDescription')" />
              <v-skeleton-loader
                v-if="isLoading && !form.description"
                type="card"
                class="input-skeleton"
              />

              <BaseMarkdownEditor
                v-else
                v-model="form.description"
                name="name"
                :placeholder="
                  $t('folder.records.createAndEditModal.messagePlaceHolder')
                "
                :with-link="false"
              />

              <div v-if="isSignatureType" class="mt-6">
                <span v-text="$t('folder.offer.fieldStdToAssociate')" />
                <base-autocomplete
                  v-model="form.signedFileStd"
                  class="selector"
                  solo
                  dense
                  :filter="() => true"
                  :search-input.sync="templateQuestion.name"
                  clearable
                  :items="templateQuestion.items"
                  item-text="name"
                  item-value="id"
                  :loading="templateQuestion.loading"
                  :label="$t('folder.offer.associateTemplateWithField')"
                >
                  <template #append-item>
                    <div v-intersect="loadMoreTemplateQuestions" />
                  </template>
                </base-autocomplete>
              </div>
            </div>
            <span class="sub-title" v-text="`Fichiers`" />
            <div class="input-fields">
              <v-skeleton-loader
                v-if="isLoading"
                type="card"
                class="input-skeleton"
              />
              <folder-offer-file-drag
                v-else
                :offer-files="form.files"
                :has-error="filesError"
                is-multiple
                :is-signature="isSignatureType"
                :need-iteration="false"
                :page-signatures="signaturePage"
                nature="secondary"
                @update-signature-page="updateSignaturePage"
                @file-droped="fileInserted"
                @delete-offer-file="removeFile"
                @file-name-changed="updateFileName"
              />
            </div>
          </div>
        </div>
        <div class="modal_footer">
          <BaseButton
            :disabled="isSubmiting"
            color="grey"
            type="secondary"
            @click="reset()"
          >
            {{ $t("utils.cancel") }}
          </BaseButton>
          <BaseButton
            :disabled="isSubmiting"
            :loading="isSubmiting"
            color="primary"
            type="primary"
            @click="submit"
          >
            {{ offerTemplateId ? $t("utils.edit") : $t("utils.create") }}
          </BaseButton>
        </div>
      </BaseCosmoCard>
    </template>
  </BaseDialog>
</template>

<script>
import { validationMixin } from "vuelidate";
import OrganizationAPI from "@/core/Services/organization.api";

import { required, maxLength, minLength } from "vuelidate/lib/validators";
import { fileURLToFile } from "@/core/Utils/file.utils";
import FolderOfferSelectePad from "../../Folder/Components/FolderOfferSelectPad/FolderOfferSelectePad.vue";
import { OFFER_TYPES } from "@/core/Utils/types.utils";
import FolderOfferFileDrag from "../../Folder/Components/FolderOffer/FolderOfferFileDrag.vue";
import { OfferTemplate } from "@/core/Models/OfferTemplate";
import { fetchQuestionTemplates } from "@/core/Services/questionTemplate.service";
import QuestionTemplate from "@/core/Models/QuestionTemplate";

export default {
  name: "OfferTemplateImportDialog",
  components: { FolderOfferSelectePad, FolderOfferFileDrag },
  mixins: [validationMixin],
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
    offerTemplateId: {
      type: Number,
      default: () => null,
    },
  },
  data() {
    return {
      loading: false,
      offerTypes: [
        {
          title: "Consultation",
          icon: "$mdiEye",
          value: OFFER_TYPES.CONSULT,
        },
        {
          title: "Validation",
          icon: "$mdiCheck",
          value: OFFER_TYPES.VALIDATION,
        },
        {
          title: "Signature électronique",
          icon: "$mdiPencilOutline",
          value: OFFER_TYPES.SIGNATURE,
        },
      ],
      isSubmiting: false,
      dragover: false,
      signedFileDatatype: null,
      form: {
        type: null,
        name: "",
        description: "",
        files: [],
        signedFileStd: null,
      },
      offerDefaultValue: null,
      templateSelected: null,
      isLoading: false,
      datatypes: {
        item: [],
        page: 1,
        next: null,
        name: null,
        searchTimeoutRef: undefined,
        count: 0,
      },
      templateQuestion: {
        items: [],
        pageSize: 10,
        page: 1,
        next: null,
        name: undefined,
        searchTimeoutRef: undefined,
      },
      signaturePage: [],
    };
  },
  validations() {
    let validations = {};
    validations.form = {
      name: {
        required,
        maxLength: maxLength(255),
      },
      type: {
        required,
      },
      files: {
        required,
        minLength: minLength(1),
        $each: {
          required,
        },
      },
    };
    return validations;
  },

  computed: {
    modalTitle() {
      return this.offerTemmplate?.id
        ? this.$t("utils.editing_offer_template")
        : this.$t("utils.adding_offer_template");
    },
    nameErrors() {
      if (!this.$v.form.name.$dirty) {
        return "";
      }
      if (!this.$v.form.name.required) {
        return this.$t("folder.records.createAndEditModal.nameRequired");
      }
      if (!this.$v.form.name.maxLength) {
        return this.$t("folder.records.createAndEditModal.nameMaxLength");
      }
      return "";
    },
    typeError() {
      if (!this.$v.form.type.$dirty) {
        return false;
      }
      if (!this.$v.form.type.required) {
        return true;
      }
      return false;
    },
    filesError() {
      if (!this.$v.form.files.$dirty) {
        return false;
      }
      if (!this.$v.form.files.required) {
        return true;
      }
      return false;
    },
    isSignatureType() {
      return this.form.type === OFFER_TYPES.SIGNATURE;
    },
  },
  watch: {
    "templateQuestion.name"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.filterQuestionTemplate();
    },
  },
  created() {
    this.fetchQuestionTemplate();
    if (this.offerTemplateId) {
      this.setTemplateOffer();
    }
  },

  methods: {
    reset() {
      this.form = {
        type: null,
        name: "",
        description: "",
        files: [],
      };
      this.$v.form.$reset();
      this.$emit("close");
    },
    async handleOfferTypeChange(type) {
      this.form.type = type;
      if (type !== OFFER_TYPES.SIGNATURE) return;
      const filterdFiles = [];
      this.form.files.forEach((file) => {
        if (!file.type.startsWith("image/")) filterdFiles.push(file);
      });
    },
    getOfferType(offertype) {
      switch (offertype) {
        case OFFER_TYPES.SIGNATURE:
          return 2;
        case OFFER_TYPES.VALIDATION:
          return 1;
        default:
          return 0;
      }
    },
    updateFileName({ value, index }) {
      const newFile = new File([this.form.files[index].file], `${value}`, {
        type: this.form.files[index].file.type,
      });
      this.form.files.splice(index, 1, {
        id: this.form.files[index].id,
        file: newFile,
      });
    },
    updateSignaturePage({ value, index }) {
      this.signaturePage.splice(index, 1, value);
    },

    async setTemplateOffer() {
      this.isLoading = true;
      const response = await OrganizationAPI.getOrganizationOfferTemplateDetail(
        {
          organizationId: this.$route.params.organizationId,
          offerTemplateId: this.offerTemplateId,
        }
      );
      const template = new OfferTemplate(response.data);
      const files = [];
      for (const file of template.files) {
        const tmpfile = await fileURLToFile(file.offerFile, file.name, true);
        files.push({
          id: file.id,
          file: tmpfile,
        });
        this.signaturePage.push(file.signaturePage);
      }
      if (template.signedFileStd) {
        this.templateQuestion.items.push(
          new QuestionTemplate(template.signedFileStd)
        );
      }

      this.form = {
        ...this.form,
        name: template.title,
        type: template.offerType,
        description: template.description,
        files: [...files],
        defaultSignedFileDatatype: template.signedFileDatatype?.id,
        signedFileStd: template.signedFileStd?.id,
      };
      this.offerDefaultValue = this.getOfferType(template.offerType);
      this.isLoading = false;
    },
    async fileInserted({ value }) {
      const fileType = [
        ".xls",
        ".xlsx",
        ".doc",
        ".docx",
        ".ppt",
        ".pptx",
        ".pdf",
      ];

      if (
        this.isSignatureType &&
        [...value].some(
          (file) => !fileType.includes(`.${file.name.split(".").pop()}`)
        )
      ) {
        return this.$store.dispatch("snackbar/active", {
          message: "Format de fichier non-supporté.",
          type: "ERROR",
        });
      }
      // insert file locally to use vuelidate+vuex
      let fileList = [];
      for (const file of value) {
        fileList.push({ id: null, file: file });
        this.signaturePage.push(1);
      }

      if (this.checkIfSameNameFile(fileList)) {
        fileList = [
          ...fileList.filter(
            (file) =>
              !this.form.files.some((item) => item.name === file.file.name)
          ),
        ];
        this.$store.dispatch("snackbar/active", {
          message: "Fichier identique",
          type: "ERROR",
        });
      }
      this.form.files = [...this.form.files, ...fileList];
      this.$v.form.files.$touch();
    },
    checkIfSameNameFile(files) {
      return files.some((file) =>
        this.form.files.some((item) => item.name === file.file.name)
      );
    },
    async removeFile(index) {
      if (this.form.files.length <= 1) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.cant_delete_last_file"),
          type: "ERROR",
        });
        return;
      }
      if (this.form.files[index].id) {
        await OrganizationAPI.deleteOrganizationOfferTemplatesFiles({
          organizationId: this.organizationId,
          offerTemplateId: this.offerTemplateId,
          offerFileId: this.form.files[index].id,
        });
        this.$store.dispatch("snackbar/active", {
          message: "Fichier supprimé avec succès",
          type: "SUCCESS",
        });
      }
      this.signaturePage.splice(index, 1);
      this.form.files.splice(index, 1);
    },
    async createOfferTemplate(payload) {
      const response = await OrganizationAPI.postOrganizationOfferTemplates({
        organizationId: this.organizationId,
        payload,
      });
      await this.createOfferTemplateFile(response.data.id);
      this.$emit("fetch-offer-templates");
      this.$store.dispatch("snackbar/active", {
        message: this.$t("utils.createdSuccessfully"),
        type: "SUCCESS",
      });
    },
    async createOfferTemplateFile(offerTemplateId) {
      const offerFileTemplatePromise = this.form.files.map((file, index) => {
        let payload = new FormData();
        payload.append("name", file.file.name);
        payload.append("offer_file", file.file);
        payload.append("signature_page", this.signaturePage[index]);
        if (file.id) {
          return OrganizationAPI.patchOrganizationOfferTemplatesFiles({
            organizationId: this.organizationId,
            payload,
            offerTemplateId,
            offerFileId: file.id,
          });
        }
        return OrganizationAPI.postOrganizationOfferTemplatesFiles({
          organizationId: this.organizationId,
          payload,
          offerTemplateId,
        });
      });
      await Promise.all(offerFileTemplatePromise);
    },
    async filterQuestionTemplate() {
      if (this.templateQuestion.loading) return;
      if (this.templateQuestion.searchTimeoutRef)
        clearTimeout(this.templateQuestion.searchTimeoutRef);
      this.templateQuestion.page = 1;
      this.templateQuestion.items = [];
      this.templateQuestion.searchTimeoutRef = setTimeout(() => {
        this.fetchQuestionTemplate();
      }, 500);
    },
    async fetchQuestionTemplate() {
      if (!this.templateQuestion.page) return;
      this.templateQuestion.loading = true;
      try {
        const response = await fetchQuestionTemplates({
          organizationId: this.$route.params.organizationId,
          page: this.templateQuestion.page,
          pageSize: this.templateQuestion.pageSize,
          name: this.templateQuestion.name,
          typeQuestion: ["MULTIPLE_FILE"],
        });
        this.templateQuestion = {
          ...this.templateQuestion,
          ...response,
          items: [...this.templateQuestion.items, ...response.items],
        };
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.templateQuestion.loading = false;
    },
    async loadMoreTemplateQuestions(_, __, isIntersecting) {
      if (!isIntersecting) return;
      this.templateQuestion.page = this.templateQuestion.next;
      await this.fetchQuestionTemplate();
    },
    async editOfferTemplate(payload) {
      await OrganizationAPI.patchOrganizationOfferTemplates({
        organizationId: this.organizationId,
        offerTemplateId: this.offerTemplateId,
        payload,
      });
      await this.createOfferTemplateFile(this.offerTemplateId);

      // files
      this.$emit("fetch-offer-templates");
      this.$store.dispatch("snackbar/active", {
        message: "Offre modifiée avec succès",
        type: "SUCCESS",
      });
    },
    async submit() {
      try {
        this.$v.form.$touch();
        if (
          this.$v.form.$invalid ||
          this.loading ||
          this.isSubmiting ||
          this.signaturePage.some((item) => item <= 0)
        )
          return;
        this.isSubmiting = true;
        let payload = {
          title: this.form.name,
          offer_type: this.form.type,
          description: this.form.description,
          is_sequential: false,
        };
        if (this.signedFileDatatype)
          payload.signed_file_datatype = this.signedFileDatatype;
        if (this.form.signedFileStd)
          payload.signed_file_std = this.form.signedFileStd;
        if (this.offerTemplateId) {
          await this.editOfferTemplate(payload);
        } else {
          await this.createOfferTemplate(payload);
        }
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.isSubmiting = false;
      this.reset();
    },
  },
};
</script>

<style lang="scss" scoped>
.modal_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding-top: 8px;
  flex-wrap: wrap;
  .modal_title {
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #353542;
  }
  .close_icon {
    margin-left: auto;
  }
}
.modal_content {
  padding-top: 24px;
  position: relative;
  .sub-title {
    font-family: "Inter";
    font-weight: 600;
    font-size: 16px;
    line-height: 19px;
    color: #353542;
  }
  .input-fields {
    padding: 16px 16px 0 16px;
    margin-bottom: 24px;
    .input_label {
      color: #353542;
      font-family: "Inter";
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px;
      margin-bottom: 16px;
    }
  }
  .offer-nature-container {
    display: flex;
    gap: 24px;
    margin-top: 24px;
    .offer-item {
      flex: 1;
    }
  }
  .file-drag {
    width: 400px;
    height: 100px;
    background: lightgrey;
  }
  ::v-deep .v-menu__content {
    top: 44px !important;
  }

  ::v-deep .v-skeleton-loader__text {
    height: 40px;
  }

  ::v-deep .v-skeleton-loader__heading .v-skeleton-loader__bone {
    display: none;
  }
  .action-container {
    display: flex;
    align-items: center;
    margin-top: 32px;
  }
}
.modal_footer {
  display: flex;
  justify-content: flex-end;
  align-content: center;
  margin-top: 16px;
  padding: 8px 0;
  gap: 16px;
}
</style>
